// ─────────────────────────────────────────────────────────────────────────────
// PHYSICS ENGINE
// Handles player movement, gravity, coyote time, variable jump, collisions,
// moving-platform carry, and jetpack thrust.
// ─────────────────────────────────────────────────────────────────────────────

import { Platform, WORLD_WIDTH, WORLD_HEIGHT } from '../data/levelData';

// ─── Constants ───────────────────────────────────────────────────────────────
const GRAVITY          = 1800;   // px / s²
const MOVE_SPEED       = 320;    // px / s  (horizontal)
const JUMP_FORCE       = -680;   // initial vy on jump
const JUMP_CUT_VY      = -200;   // vy clamped to this when jump released early
const MAX_FALL         = 900;    // terminal velocity
const COYOTE_TIME      = 0.1;    // seconds of grace after walking off edge
const JUMP_BUFFER_TIME = 0.12;   // seconds of pre-land jump buffering
const PLAYER_W         = 28;
const PLAYER_H         = 48;
const JETPACK_THRUST   = 1400;   // upward accel while thrusting
const JETPACK_MAX_UP   = -420;   // cap upward speed with jetpack

export type PhysicsBody = {
  x:       number;
  y:       number;
  vx:      number;
  vy:      number;
  w:       number;
  h:       number;
  onGround: boolean;
  coyoteTimer:      number;
  jumpBufferTimer:  number;
  facingRight:      boolean;
  /** Index of platform currently stood on, or -1 */
  groundPlatform: number;
};

export function createPlayer(startX: number, startY: number): PhysicsBody {
  return {
    x: startX,
    y: startY,
    vx: 0,
    vy: 0,
    w: PLAYER_W,
    h: PLAYER_H,
    onGround: false,
    coyoteTimer:     0,
    jumpBufferTimer: 0,
    facingRight:     true,
    groundPlatform:  -1,
  };
}

// ─── AABB helpers ────────────────────────────────────────────────────────────
function overlaps(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number,
): boolean {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

/**
 * Apply moving-platform delta to the player if they were riding one.
 * Call BEFORE stepPhysics with the previous→current offset delta.
 */
export function carryOnPlatform(
  body: PhysicsBody,
  prevOffsets: Map<number, { ox: number; oy: number }>,
  nextOffsets: Map<number, { ox: number; oy: number }>,
): void {
  if (body.groundPlatform < 0) return;
  const prev = prevOffsets.get(body.groundPlatform) ?? { ox: 0, oy: 0 };
  const next = nextOffsets.get(body.groundPlatform) ?? { ox: 0, oy: 0 };
  body.x += next.ox - prev.ox;
  body.y += next.oy - prev.oy;
}

// ─── Step ────────────────────────────────────────────────────────────────────
export function stepPhysics(
  body:      PhysicsBody,
  dt:        number,
  axisX:     number,   // -1 | 0 | 1
  doJump:    boolean,
  jumpIsHeld:boolean,
  platforms: Platform[],
  movingOffsets: Map<number, { ox: number; oy: number }>,
  jetpackThrust: boolean,
): 'ok' | 'dead' {

  // ── Horizontal ─────────────────────────────────────────────────────────────
  body.vx = axisX * MOVE_SPEED;
  if (axisX > 0) body.facingRight = true;
  if (axisX < 0) body.facingRight = false;

  // ── Jump buffering ──────────────────────────────────────────────────────────
  if (doJump) body.jumpBufferTimer = JUMP_BUFFER_TIME;
  else        body.jumpBufferTimer = Math.max(0, body.jumpBufferTimer - dt);

  // ── Gravity / jetpack ───────────────────────────────────────────────────────
  if (jetpackThrust) {
    body.vy -= JETPACK_THRUST * dt;
    if (body.vy < JETPACK_MAX_UP) body.vy = JETPACK_MAX_UP;
  } else {
    body.vy += GRAVITY * dt;
  }
  if (body.vy > MAX_FALL) body.vy = MAX_FALL;

  // ── Variable jump: cut apex when key released ────────────────────────────
  if (!jumpIsHeld && !jetpackThrust && body.vy < JUMP_CUT_VY) body.vy = JUMP_CUT_VY;

  // ── Coyote time tracking ────────────────────────────────────────────────────
  const wasOnGround = body.onGround;
  if (wasOnGround) body.coyoteTimer = COYOTE_TIME;
  else             body.coyoteTimer = Math.max(0, body.coyoteTimer - dt);

  // ── Apply jump ─────────────────────────────────────────────────────────────
  const canJump = body.coyoteTimer > 0 && body.jumpBufferTimer > 0;
  if (canJump) {
    body.vy             = JUMP_FORCE;
    body.coyoteTimer    = 0;
    body.jumpBufferTimer = 0;
  }

  // ── Move X ─────────────────────────────────────────────────────────────────
  body.x += body.vx * dt;

  // Clamp to world
  if (body.x < 0) body.x = 0;
  if (body.x + body.w > WORLD_WIDTH) body.x = WORLD_WIDTH - body.w;

  // ── Move Y ─────────────────────────────────────────────────────────────────
  body.y += body.vy * dt;

  // ── Platform collision ──────────────────────────────────────────────────────
  body.onGround = false;
  body.groundPlatform = -1;
  for (let i = 0; i < platforms.length; i++) {
    const p  = platforms[i];
    const mo = movingOffsets.get(i);
    const px = p.x + (mo?.ox ?? 0);
    const py = p.y + (mo?.oy ?? 0);

    if (!overlaps(body.x, body.y, body.w, body.h, px, py, p.w, p.h)) continue;

    const fromTop    = Math.abs((body.y + body.h) - py);
    const fromBottom = Math.abs(body.y - (py + p.h));
    const fromLeft   = Math.abs((body.x + body.w) - px);
    const fromRight  = Math.abs(body.x - (px + p.w));
    const min        = Math.min(fromTop, fromBottom, fromLeft, fromRight);

    if (min === fromTop && body.vy >= 0) {
      body.y       = py - body.h;
      body.vy      = 0;
      body.onGround = true;
      body.groundPlatform = i;
    } else if (min === fromBottom && body.vy < 0) {
      body.y  = py + p.h;
      body.vy = 0;
    } else if (min === fromLeft) {
      body.x  = px - body.w;
      body.vx = 0;
    } else if (min === fromRight) {
      body.x  = px + p.w;
      body.vx = 0;
    }
  }

  // ── Fall out of world → dead ────────────────────────────────────────────────
  if (body.y > WORLD_HEIGHT + 200) return 'dead';

  return 'ok';
}

/**
 * Expanded landing / proximity check for info triggers.
 * Uses a wider & taller AABB so near-misses still count.
 */
export function nearPlatformTop(
  body: PhysicsBody,
  p: Platform,
  ox: number,
  oy: number,
  padX = 48,
  padY = 36,
): boolean {
  const px = p.x + ox - padX;
  const py = p.y + oy - padY;
  const pw = p.w + padX * 2;
  const ph = p.h + padY * 2;
  return overlaps(body.x, body.y, body.w, body.h, px, py, pw, ph);
}
